import {
  CloudUploadOutlined,
  FireOutlined,
  HistoryOutlined,
  IdcardOutlined,
  LogoutOutlined,
  UnorderedListOutlined,
  UsergroupAddOutlined,
  UserOutlined,
  MoreOutlined,
  BugOutlined,
  MergeCellsOutlined,
} from '@ant-design/icons';
import { MenuProps, message } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';
import authService from '../shared/service/authService';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_PAGE = 1;
const DEFAULT_PAGINATION_PATH = `?page=${DEFAULT_PAGE}&page_size=${DEFAULT_PAGE_SIZE}`;

export const COMMON_ADMIN_MENU_KEY_LIST = ['account', 'logout', 'action-history'];
export const CP_MENU_KEY_LIST = [
  ...COMMON_ADMIN_MENU_KEY_LIST,
  'user',
  'import-upload',
  'content',
  // 'playlist',
  'metadata',
  'company',
  'assets',
  // platform 2(CP) 계정도 vietnam 권한을 받을 수 있다. flag가 꺼진 CP 계정은 이 필터를 타므로
  // 여기 없으면 권한이 있어도 메뉴가 사라진다.
  'vietnam-uploads',
];

// LG Channel Vietnam 전용 계정에게 항상 보장되는 기본 메뉴.
// 실제 vietnam 메뉴는 보유 permission code에서 유도된다 (accountStore.getMenus 참고).
//
// `import-upload`를 넣지 않는 이유: BE ScopedJWTAuthentication allowlist가
// /api/v1/vietnam-upload/ 와 /api/v1/accounts/ 뿐이라, Import & Upload 화면이 호출하는
// content API가 전부 403이 된다. 업로드 진입은 Vietnam Video 화면의 Upload 버튼이 담당한다.
export const VIETNAM_BASE_MENU_KEY_LIST = [...COMMON_ADMIN_MENU_KEY_LIST];

export const META_DATA_STRUCTURE_MENU_ITEMS: MenuProps['items'] = [
  {
    key: 'people',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/people${DEFAULT_PAGINATION_PATH}`}>People</Link>,
  },
  {
    key: 'tag',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/tag${DEFAULT_PAGINATION_PATH}`}>Tag</Link>,
  },
  {
    key: 'genre',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/genre${DEFAULT_PAGINATION_PATH}`}>Genre</Link>,
  },
  {
    key: 'category',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/category${DEFAULT_PAGINATION_PATH}`}>Category</Link>,
  },
  {
    key: 'global-setting',
    icon: <UnorderedListOutlined />,
    label: <Link to="/global-setting">Global Setting</Link>,
  },
];

export const CONTRACT_MENU_ITEMS: MenuProps['items'] = [
  {
    key: 'contract',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/contract${DEFAULT_PAGINATION_PATH}`}>Contract</Link>,
  },
  {
    key: 'company',
    icon: <UsergroupAddOutlined />,
    label: <Link to={`/content-provider${DEFAULT_PAGINATION_PATH}`}>Content Provider</Link>,
  },
];

export const CONTENT_MENU_ITEMS: MenuProps['items'] = [
  {
    key: 'import-upload',
    icon: <CloudUploadOutlined />,
    label: <Link to="/import-upload">Import & Upload</Link>,
  },
  // 목록은 화면 하나로 통합돼 있다. 자기 company만 보이는지 전 CP가 보이는지는 서버가 정하므로
  // 메뉴/라우트를 CP용·운영진용으로 나누지 않는다.
  {
    key: 'vietnam-uploads',
    icon: <CloudUploadOutlined />,
    label: <Link to="/vietnam-uploads">Vietnam Video</Link>,
  },
  // 업로드 화면. VN 전용 계정에만 노출된다 (accountStore.getMenus의 VN 분기).
  // ODK 운영진은 Import & Upload의 "Vietnam Direct Upload" 버튼으로 진입한다 —
  // 운영진 사이드바에 업로드 메뉴를 하나 더 늘리지 않기 위해서다.
  {
    key: 'vietnam-upload-new',
    icon: <CloudUploadOutlined />,
    label: <Link to="/assets/add-vn">Vietnam Direct Upload</Link>,
  },
  {
    key: 'assets',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/assets${DEFAULT_PAGINATION_PATH}`}>Video Assets</Link>,
  },
  {
    key: 'series',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/series${DEFAULT_PAGINATION_PATH}`}>Series</Link>,
  },
  {
    key: 'season',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/season${DEFAULT_PAGINATION_PATH}`}>Season</Link>,
  },
  {
    key: 'movie',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/movie${DEFAULT_PAGINATION_PATH}`}>Movie</Link>,
  },
  {
    key: 'content',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/library${DEFAULT_PAGINATION_PATH}`}>Episode</Link>,
  },
  {
    key: 'playlist',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/playlists${DEFAULT_PAGINATION_PATH}`}>MRSS Feed</Link>,
  },
  {
    key: 'burn-in',
    icon: <FireOutlined />,
    label: <Link to={`/burn-in${DEFAULT_PAGINATION_PATH}`}>Subtitle Burn-in</Link>,
  },
];

export const ADMIN_MENU_ITEMS: MenuProps['items'] = [
  { key: 'account', icon: <IdcardOutlined />, label: <Link to="/account">Profile</Link> },
  {
    key: 'user',
    icon: <UserOutlined />,
    label: <Link to={`/user${DEFAULT_PAGINATION_PATH}`}>User Management</Link>,
  },
  {
    key: 'role',
    icon: <UnorderedListOutlined />,
    label: <Link to="/role">Role</Link>,
  },
  {
    key: 'action-history',
    icon: <HistoryOutlined />,
    label: <Link to={`/action-history${DEFAULT_PAGINATION_PATH}`}>Action History</Link>,
  },
  {
    key: 'logout',
    icon: <LogoutOutlined />,
    label: 'Logout',
    onClick: async (): Promise<void> => {
      await authService.logout();
      message.success('Logout success');
      window.location.reload();
    },
  },
];

export const REVENUE_MENU_ITEMS: MenuProps['items'] = [
  {
    key: 'revenue-share',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/revenue-share${DEFAULT_PAGINATION_PATH}`}>Revenue Share</Link>,
  },
  {
    key: 'revenue-dashboard',
    icon: <UnorderedListOutlined />,
    label: <Link to="/revenue-dashboard">Revenue Dashboard</Link>,
  },
];

export const PERMISSION_MENU_ITEMS: MenuProps['items'] = [
  {
    key: 'role',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/role${DEFAULT_PAGINATION_PATH}`}>Role</Link>,
  },
];

export const INGEST_MENU_ITEMS: MenuProps['items'] = [
  {
    key: 'kcp-ingest',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/kcp-ingest${DEFAULT_PAGINATION_PATH}`}>KCP Ingest List</Link>,
  },
  {
    key: 'kcp-info',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/kcp-info${DEFAULT_PAGINATION_PATH}`}>KCP Info List</Link>,
  },
  {
    key: 'mediahold-ingest',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/mediahold-ingest${DEFAULT_PAGINATION_PATH}`}>Mediahold Ingest History</Link>,
  },
  {
    key: 'title-auto-generator',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/title-auto-generator${DEFAULT_PAGINATION_PATH}`}>Title Auto Generator</Link>,
  },
];

export const SYNC_MENU_ITEMS: MenuProps['items'] = [
  {
    key: 'video-sync-monitor',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/video-sync-monitor${DEFAULT_PAGINATION_PATH}`}>KCP/Mediahold Sync Status</Link>,
  },
  {
    key: 'inhouse-sync-monitor',
    icon: <UnorderedListOutlined />,
    label: <Link to={`/inhouse-sync-monitor${DEFAULT_PAGINATION_PATH}`}>Inhouse Sync Monitor</Link>,
  },
];

export const VENDOR_MENU_ITEMS: MenuProps['items'] = [
  {
    key: 'vendor-subtitle',
    icon: <UnorderedListOutlined />,
    label: <Link to="/vendor/subtitle">Subtitle</Link>,
  },
  {
    key: 'vendor-download',
    icon: <UnorderedListOutlined />,
    label: <Link to="/vendor/download">Download</Link>,
  },
];

export const TOOLS_MENU_ITEMS: MenuProps['items'] = [
  {
    key: 'debug',
    icon: <BugOutlined />,
    label: <Link to="/debug">Debug</Link>,
  },
  {
    key: 'content-relay',
    icon: <MergeCellsOutlined />,
    label: <Link to="/content-relay">Content Relay</Link>,
  },
];
